/**
 * Calendar date math — plain `Date`, no dependency (date-fns is not installed).
 * Mirrors the helpers in `app/src/app/calendar.tsx` so both apps agree on day
 * boundaries: air dates are date-only strings ("2026-08-09") parsed as LOCAL
 * calendar days, and weeks start on Sunday.
 */

/** Local-day key — `2026-8-3` style, equality-only (same as the app). */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export function sameDay(a: Date, b: Date): boolean {
  return dayKey(a) === dayKey(b);
}

/**
 * Parse a date-only air string ("2026-08-09") as a LOCAL calendar day.
 * `new Date("2026-08-09")` is UTC midnight, which shifts a day backwards in
 * western timezones and would put Sunday episodes on Saturday.
 */
export function airDay(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y || 2026, (m || 1) - 1, d || 1);
}

/** Midnight of the local day — the "coming up" lower bound. */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** 42 cells (6 weeks) covering the month, Sunday-first (same as the app). */
export function monthGrid(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

/** `S1 E4` — the code that follows the show in a row label (ui-ux.md §2). */
export function seasonEpisodeLabel(season: number, episode: number): string {
  return `S${season} E${episode}`;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** `2026-08-09` — the `?day=` query value for a local day. */
export function dayParam(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** `2026-08` — the `?month=` query value. */
export function monthParam(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

/** `?day=YYYY-MM-DD` → a local day; null when absent or not a real date. */
export function parseDayParam(value: string | null): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return dayParam(d) === value ? d : null;
}

/** `?month=YYYY-MM` → the 1st of that month; null when absent or invalid. */
export function parseMonthParam(value: string | null): Date | null {
  const m = /^(\d{4})-(\d{2})$/.exec(value ?? '');
  if (!m || Number(m[2]) < 1 || Number(m[2]) > 12) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, 1);
}

/** Drop placeholder episode titles like "Episode 14" / "Episódio 14". */
export function isPlaceholderTitle(title: string): boolean {
  return !title || /^(episode|episódio)\s*\d+$/i.test(title.trim());
}

/** The Intl locale string for a ScreenAtlas locale. */
export function intlLocale(locale: 'en' | 'pt-BR'): string {
  return locale === 'pt-BR' ? 'pt-BR' : 'en-US';
}

export function formatMonthLabel(month: Date, locale: 'en' | 'pt-BR' = 'en'): string {
  return month.toLocaleDateString(intlLocale(locale), { month: 'long', year: 'numeric' });
}

export function formatFullDay(d: Date, locale: 'en' | 'pt-BR' = 'en'): string {
  return d.toLocaleDateString(intlLocale(locale), {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/** "Sunday, Oct 18" — a "Coming up" day heading (no year: the list spans weeks). */
export function formatAgendaDay(d: Date, locale: 'en' | 'pt-BR' = 'en'): string {
  return d.toLocaleDateString(intlLocale(locale), {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

export function formatShortDay(d: Date, locale: 'en' | 'pt-BR' = 'en'): string {
  return d.toLocaleDateString(intlLocale(locale), { month: 'short', day: 'numeric' });
}

/**
 * Sunday-first full weekday names for the grid header. The visible letter is
 * `charAt(0)` — en yields S/M/T/W/T/F/S and pt-BR D/S/T/Q/Q/S/S, matching the
 * app's compact strip.
 */
export function weekdayNames(locale: 'en' | 'pt-BR'): string[] {
  const fmt = new Intl.DateTimeFormat(intlLocale(locale), { weekday: 'long' });
  const sunday = new Date(2026, 0, 4); // a known Sunday
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(sunday);
    day.setDate(sunday.getDate() + i);
    return fmt.format(day);
  });
}

/** "today" / "tomorrow" / "yesterday" relative to `today`, or null — the day headings' lead word. */
export function relativeDay(d: Date, today: Date): 'today' | 'tomorrow' | 'yesterday' | null {
  const diff = Math.round((startOfDay(d).getTime() - startOfDay(today).getTime()) / 86_400_000);
  return diff === 0 ? 'today' : diff === 1 ? 'tomorrow' : diff === -1 ? 'yesterday' : null;
}
